import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Container from "../Container";
import Footer from "../Footer";

const PrivacyPolicyPage: React.FunctionComponent = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Container className="pt-28 pb-16">
        <div className="max-w-3xl">
          <Heading level={1} variant="lg" className="mb-2">
            Privacy Policy
          </Heading>
          <Text variant="sm" className="text-muted-foreground mb-10">
            Effective date: 30 March 2026
          </Text>

          {/* 1. Introduction */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            1. Introduction
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Punch Cards Ltd (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the Punch Cards
            service at punch.cards. This privacy policy explains how we collect,
            use, and protect personal data in accordance with the UK General Data
            Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            By using our service, you acknowledge that you have read and
            understood this policy. If you do not agree with our practices,
            please do not use the service.
          </Text>

          {/* 2. Data Controller */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            2. Data Controller
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Punch Cards Ltd is the data controller responsible for your personal
            data. If you have any questions about this policy or our data
            practices, you can contact our Privacy Officer at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            .
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            Punch Cards Ltd has assessed that it is not required to appoint a
            formally designated Data Protection Officer (DPO) under UK GDPR, as
            our core activities do not involve large-scale systematic monitoring
            or processing of special category data. Data protection queries
            should be directed to our Privacy Officer using the contact details
            above.
          </Text>

          {/* 3. What Data We Collect */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            3. What Data We Collect
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We collect and process the following categories of personal data:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>Account information:</strong> your name, email address,
                and company name, provided when you register for the service.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Workspace configuration:</strong> your scoring
                preferences, team structure, and integration settings.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Employee identifiers:</strong> names and platform
                identifiers of employees within your organisation, retrieved from
                your connected platforms (such as Slack and Intercom).
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Aggregate performance metrics:</strong> numerical counts
                of actions taken within your connected platforms, including reply
                counts, ticket closure counts, and CSAT ratings.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Usage data:</strong> log data, browser type, device
                information, and pages visited within the service.
              </Text>
            </li>
          </ul>

          {/* 4. Data We Do Not Collect */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            4. Data We Do Not Collect
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We want to be clear about the boundaries of our data processing. We
            do <strong>not</strong> collect, access, process, or store:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                The content of customer support tickets or conversations.
              </Text>
            </li>
            <li>
              <Text variant="md">
                The content of internal messages (e.g., Slack messages).
              </Text>
            </li>
            <li>
              <Text variant="md">
                Personal data belonging to your customers or end users.
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            Our integrations with connected platforms retrieve only aggregate
            action counts and metadata necessary to generate performance scores.
          </Text>

          {/* 5. How We Use Your Data */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            5. How We Use Your Data
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We process personal data on the following lawful bases under Article
            6 of the UK GDPR:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>Performance of a contract (Art. 6(1)(b)):</strong> to
                provide and maintain the Punch Cards service, generate
                performance scores, and manage your account.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Legitimate interests (Art. 6(1)(f)):</strong> to improve
                the service, conduct analytics, ensure security, and prevent
                fraud. We have assessed that these interests do not override your
                rights and freedoms.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Consent (Art. 6(1)(a)):</strong> where applicable, for
                marketing communications. You may withdraw consent at any time
                by contacting us at{" "}
                <a
                  href="mailto:privacy@punch.cards"
                  className="text-foreground underline hover:text-highlight"
                >
                  privacy@punch.cards
                </a>{" "}
                or by using the unsubscribe link in any marketing email we send.
              </Text>
            </li>
          </ul>

          {/* 6. Data from Connected Platforms */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            6. Data from Connected Platforms
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            When you connect your messaging systems (e.g., Slack) or agent
            systems (e.g., Intercom) to Punch Cards, we access these platforms
            via their official, authorised API integrations. We request only the
            minimum permissions necessary to retrieve aggregate action data.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            The data we retrieve from connected platforms is limited to numerical
            metrics and employee identifiers. We use this data solely to
            calculate and deliver performance scores. We do not read, store, or
            process the content of any messages, tickets, or conversations.
          </Text>

          {/* 7. Data Sharing */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            7. Data Sharing
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We do not sell your personal data to third parties. We may share
            personal data with the following categories of recipients, solely to
            operate and improve the service:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>Infrastructure and hosting providers</strong> who store
                and process data on our behalf.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Analytics providers</strong> who help us understand how
                the service is used.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Payment processors</strong> who handle subscription
                billing (see section 9).
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            All sub-processors are located in the UK or in jurisdictions covered
            by a UK adequacy decision, or are bound by appropriate safeguards
            such as the International Data Transfer Agreement (IDTA) or UK
            Addendum to EU Standard Contractual Clauses.
          </Text>

          {/* 8. Data Retention */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            8. Data Retention
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We apply the following retention periods to the personal data we
            hold:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>Account data and performance metrics:</strong> retained
                for the duration of your active subscription and deleted within
                90 days of account closure, unless a deletion request is
                received sooner or retention is required by law.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Usage and log data:</strong> retained for up to 12
                months from the date of collection, after which it is deleted or
                anonymised.
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            You may request deletion of your personal data at any time by
            contacting us at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            . We will action your request within 30 days unless we are required
            to retain the data by law.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            Anonymised and aggregated data that cannot be used to identify any
            individual may be retained for analytical purposes indefinitely.
          </Text>

          {/* 9. Payment Information */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            9. Payment Information
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Subscription payments are processed by a third-party payment
            provider. We do not directly collect, store, or have access to your
            full payment card details. Our payment processor handles your payment
            information in accordance with the Payment Card Industry Data
            Security Standard (PCI DSS).
          </Text>

          {/* 10. Your Rights */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            10. Your Rights Under UK GDPR
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Under the UK GDPR, you have the following rights in relation to your
            personal data:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>Right of access:</strong> to request a copy of the
                personal data we hold about you.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to rectification:</strong> to request correction of
                inaccurate or incomplete data.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to erasure:</strong> to request deletion of your
                personal data, subject to legal retention requirements.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to restriction:</strong> to request that we limit
                the processing of your data in certain circumstances.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to data portability:</strong> to receive your data
                in a structured, commonly used, machine-readable format.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to object:</strong> to object to processing based
                on legitimate interests or for direct marketing purposes.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Right to be informed:</strong> to receive clear and
                transparent information about how we collect and use your
                personal data. This privacy policy fulfils this right.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>Rights in relation to automated decision-making:</strong>{" "}
                to not be subject to solely automated decisions that produce
                legal or similarly significant effects. We do not use automated
                decision-making or profiling as defined under Article 22 of the
                UK GDPR.
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            To exercise any of these rights, please contact us at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            . We will respond to your request within one month, as required by
            law.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            If you have a concern about how we handle your personal data, we
            encourage you to contact us first at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            . We will acknowledge your complaint within 30 days and work to
            resolve it promptly.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            If you remain dissatisfied, you have the right to lodge a complaint
            with the Information Commissioner&apos;s Office (ICO), the UK&apos;s
            supervisory authority for data protection. You can contact the ICO
            at{" "}
            <a
              href="https://ico.org.uk"
              className="text-foreground underline hover:text-highlight"
              target="_blank"
              rel="noopener noreferrer"
            >
              ico.org.uk
            </a>{" "}
            or by telephone on 0303 123 1113.
          </Text>

          {/* 11. International Transfers */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            11. International Transfers
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Where personal data is transferred outside the United Kingdom, we
            ensure that appropriate safeguards are in place, including the UK
            International Data Transfer Agreement (IDTA), the UK Addendum to EU
            Standard Contractual Clauses, or reliance on a UK adequacy decision,
            as applicable.
          </Text>

          {/* 12. Security */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            12. Security
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We implement appropriate technical and organisational measures to
            protect your personal data against unauthorised access, alteration,
            disclosure, or destruction. These measures include encryption of data
            in transit and at rest, access controls, and regular security
            reviews. However, no method of transmission over the internet or
            electronic storage is completely secure, and we cannot guarantee
            absolute security.
          </Text>

          {/* 13. Cookies */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            13. Cookies
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We use strictly necessary cookies for session management and
            authentication. These cookies are essential for the operation of the
            service and do not require your consent. We do not use third-party
            advertising or tracking cookies.
          </Text>

          {/* 14. Changes */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            14. Changes to This Policy
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We may update this privacy policy from time to time. If we make
            material changes, we will notify you by email or through a prominent
            notice within the service prior to the changes taking effect. We
            encourage you to review this policy periodically.
          </Text>

          {/* 15. Contact */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            15. Contact Us
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            If you have any questions about this privacy policy or wish to
            exercise your data protection rights, please contact our Privacy
            Officer at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            .
          </Text>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
